import { useEffect } from 'react';

const ACTIVE_STATE = {
  NONE: 'none',
  BANNER: 'banner',
  PREFERENCE_CENTER: 'preference-center',
};
const BANNER_ID = 'onetrust-banner-sdk';
const COOKIE_SETTINGS_BUTTON_ID = 'onetrust-pc-btn-handler';
const ONE_TRUST_ROOT_ID = 'onetrust-consent-sdk';
const PREFERENCE_CENTER_ID = 'onetrust-pc-sdk';
const OBSERVED_ATTRIBUTE_NAMES = ['class', 'style', 'aria-hidden'];
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[role="button"]',
  '[role="switch"]',
  '[role="tab"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const isVisible = (element) => {
  if (!element) {
    return false;
  }

  return (
    !element.hidden
    && !element.classList.contains('ot-hide')
    && element.getAttribute('aria-hidden') !== 'true'
    && element.style.display !== 'none'
    && element.getClientRects().length > 0
  );
};

const isPreferenceCenterOpen = (preferenceCenter) => {
  return isVisible(preferenceCenter);
};

const getFocusableElements = (container) => {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => isVisible(element)
  );
};

const focusElement = (element) => {
  if (element && typeof element.focus === 'function') {
    element.focus();
  }
};

const focusFirstElement = (container) => {
  const [firstFocusableElement] = getFocusableElements(container);

  focusElement(firstFocusableElement);
};

const focusPageStart = () => {
  const skipLink = document.querySelector('[data-skip-to-content]');

  if (skipLink) {
    focusElement(skipLink);
  }
};

const getActiveState = ({ banner, preferenceCenter }) => {
  if (isPreferenceCenterOpen(preferenceCenter)) {
    return ACTIVE_STATE.PREFERENCE_CENTER;
  }

  if (isVisible(banner)) {
    return ACTIVE_STATE.BANNER;
  }

  return ACTIVE_STATE.NONE;
};

const getActiveContainer = ({ activeState, banner, preferenceCenter }) => {
  if (activeState === ACTIVE_STATE.PREFERENCE_CENTER) {
    return preferenceCenter;
  }

  if (activeState === ACTIVE_STATE.BANNER) {
    return banner;
  }

  return null;
};

const useOneTrustAccessibility = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const hiddenElementStates = new Map();

    let activeState = ACTIVE_STATE.NONE;
    let bannerObserver;
    let preferenceCenterObserver;
    let oneTrustRootObserver;
    let observedBanner;
    let observedPreferenceCenter;
    let observedOneTrustRoot;
    let previousBannerFocusTarget;

    const setElementAriaHiddenState = (element, shouldHide) => {
      if (!element) {
        return;
      }

      const previousAriaHiddenValue = element.getAttribute('aria-hidden');

      if (shouldHide) {
        if (!hiddenElementStates.has(element)) {
          hiddenElementStates.set(element, previousAriaHiddenValue);
        }

        element.setAttribute('aria-hidden', 'true');
        return;
      }

      const storedAriaHiddenValue = hiddenElementStates.get(element);

      if (typeof storedAriaHiddenValue === 'undefined') {
        return;
      }

      if (storedAriaHiddenValue === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', storedAriaHiddenValue);
      }

      hiddenElementStates.delete(element);
    };

    const setBackgroundAriaHidden = (shouldHide, oneTrustRoot) => {
      const bodyChildren = Array.from(document.body.children).filter(
        (element) => element !== oneTrustRoot
      );

      bodyChildren.forEach((element) => {
        setElementAriaHiddenState(element, shouldHide);
      });
    };

    const syncCookieSettingsButton = () => {
      const cookieSettingsButton = document.getElementById(COOKIE_SETTINGS_BUTTON_ID);
      const preferenceCenter = document.getElementById(PREFERENCE_CENTER_ID);

      if (!cookieSettingsButton) {
        return;
      }

      cookieSettingsButton.setAttribute('aria-haspopup', 'dialog');
      cookieSettingsButton.setAttribute('aria-expanded', String(isPreferenceCenterOpen(preferenceCenter)));

      if (preferenceCenter) {
        cookieSettingsButton.setAttribute('aria-controls', PREFERENCE_CENTER_ID);
      }
    };

    const syncBannerSemantics = () => {
      const banner = document.getElementById(BANNER_ID);
      const bannerDialog = banner?.querySelector('[role="alertdialog"]');

      if (banner) {
        banner.removeAttribute('tabindex');
      }

      if (bannerDialog) {
        bannerDialog.setAttribute('aria-modal', 'true');
      }
    };

    const syncActiveLayerState = () => {
      const oneTrustRoot = document.getElementById(ONE_TRUST_ROOT_ID);
      const banner = document.getElementById(BANNER_ID);
      const preferenceCenter = document.getElementById(PREFERENCE_CENTER_ID);
      const nextActiveState = getActiveState({
        banner,
        preferenceCenter,
      });
      const nextActiveContainer = getActiveContainer({
        activeState: nextActiveState,
        banner,
        preferenceCenter,
      });

      syncCookieSettingsButton();
      syncBannerSemantics();
      setBackgroundAriaHidden(nextActiveState !== ACTIVE_STATE.NONE, oneTrustRoot);
      setElementAriaHiddenState(
        banner,
        nextActiveState === ACTIVE_STATE.PREFERENCE_CENTER
      );

      if (
        activeState === ACTIVE_STATE.BANNER
        && nextActiveState === ACTIVE_STATE.PREFERENCE_CENTER
      ) {
        previousBannerFocusTarget = banner?.contains(document.activeElement)
          ? document.activeElement
          : document.getElementById(COOKIE_SETTINGS_BUTTON_ID);
      }

      if (activeState !== nextActiveState) {
        window.requestAnimationFrame(() => {
          if (nextActiveState === ACTIVE_STATE.PREFERENCE_CENTER) {
            focusFirstElement(preferenceCenter);
            return;
          }

          if (
            activeState === ACTIVE_STATE.PREFERENCE_CENTER
            && nextActiveState === ACTIVE_STATE.BANNER
          ) {
            if (
              previousBannerFocusTarget
              && banner?.contains(previousBannerFocusTarget)
              && isVisible(previousBannerFocusTarget)
            ) {
              focusElement(previousBannerFocusTarget);
              return;
            }

            focusFirstElement(banner);
            return;
          }

          if (nextActiveState === ACTIVE_STATE.BANNER) {
            focusFirstElement(banner);
            return;
          }

          if (activeState !== ACTIVE_STATE.NONE) {
            focusPageStart();
          }
        });
      } else if (
        nextActiveContainer
        && !nextActiveContainer.contains(document.activeElement)
      ) {
        window.requestAnimationFrame(() => {
          focusFirstElement(nextActiveContainer);
        });
      }

      activeState = nextActiveState;
    };

    const observeBanner = () => {
      const nextBanner = document.getElementById(BANNER_ID);

      if (nextBanner === observedBanner) {
        return;
      }

      if (bannerObserver) {
        bannerObserver.disconnect();
      }

      observedBanner = nextBanner;

      if (!observedBanner) {
        bannerObserver = undefined;
        return;
      }

      bannerObserver = new MutationObserver(syncActiveLayerState);
      bannerObserver.observe(observedBanner, {
        attributes: true,
        attributeFilter: OBSERVED_ATTRIBUTE_NAMES,
      });
    };

    const observePreferenceCenter = () => {
      const nextPreferenceCenter = document.getElementById(PREFERENCE_CENTER_ID);

      if (nextPreferenceCenter === observedPreferenceCenter) {
        return;
      }

      if (preferenceCenterObserver) {
        preferenceCenterObserver.disconnect();
      }

      observedPreferenceCenter = nextPreferenceCenter;

      if (!observedPreferenceCenter) {
        preferenceCenterObserver = undefined;
        return;
      }

      preferenceCenterObserver = new MutationObserver(syncActiveLayerState);
      preferenceCenterObserver.observe(observedPreferenceCenter, {
        attributes: true,
        attributeFilter: OBSERVED_ATTRIBUTE_NAMES,
      });
    };

    const observeOneTrustRoot = () => {
      const nextOneTrustRoot = document.getElementById(ONE_TRUST_ROOT_ID);

      if (nextOneTrustRoot === observedOneTrustRoot) {
        return;
      }

      if (oneTrustRootObserver) {
        oneTrustRootObserver.disconnect();
      }

      observedOneTrustRoot = nextOneTrustRoot;

      if (!observedOneTrustRoot) {
        oneTrustRootObserver = undefined;
        return;
      }

      oneTrustRootObserver = new MutationObserver(() => {
        observeBanner();
        observePreferenceCenter();
        syncActiveLayerState();
      });
      oneTrustRootObserver.observe(observedOneTrustRoot, {
        childList: true,
        subtree: true,
      });
    };

    const getCurrentActiveContainer = () => {
      const banner = document.getElementById(BANNER_ID);
      const preferenceCenter = document.getElementById(PREFERENCE_CENTER_ID);

      return getActiveContainer({
        activeState,
        banner,
        preferenceCenter,
      });
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key !== 'Tab') {
        return;
      }

      const activeContainer = getCurrentActiveContainer();

      if (!activeContainer) {
        return;
      }

      const focusableElements = getFocusableElements(activeContainer);

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const { activeElement } = document;
      const isFocusInsideActiveContainer = activeContainer.contains(activeElement);

      if (!isFocusInsideActiveContainer) {
        event.preventDefault();
        focusElement(event.shiftKey ? lastFocusableElement : firstFocusableElement);
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        focusElement(firstFocusableElement);
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        focusElement(lastFocusableElement);
      }
    };

    const applyCookieSettingsAccessibility = () => {
      observeOneTrustRoot();
      observeBanner();
      observePreferenceCenter();
      syncActiveLayerState();
    };

    const previousOptanonWrapper = window.OptanonWrapper;
    const wrappedOptanonWrapper = () => {
      if (typeof previousOptanonWrapper === 'function') {
        previousOptanonWrapper();
      }

      applyCookieSettingsAccessibility();
    };

    window.OptanonWrapper = wrappedOptanonWrapper;
    document.addEventListener('keydown', handleDocumentKeyDown, true);
    applyCookieSettingsAccessibility();

    return () => {
      if (window.OptanonWrapper === wrappedOptanonWrapper) {
        window.OptanonWrapper = previousOptanonWrapper;
      }

      document.removeEventListener('keydown', handleDocumentKeyDown, true);

      if (bannerObserver) {
        bannerObserver.disconnect();
      }

      if (preferenceCenterObserver) {
        preferenceCenterObserver.disconnect();
      }

      if (oneTrustRootObserver) {
        oneTrustRootObserver.disconnect();
      }

      Array.from(hiddenElementStates.keys()).forEach((element) => {
        setElementAriaHiddenState(element, false);
      });
    };
  }, []);
};

export default useOneTrustAccessibility;
